// Unified client configuration variable
// Note: VITE_CLIENT_CONFIG is used for frontend build-time config
// CLIENT_CONFIG is used for backend runtime config
// import.meta.env is absent under the node test runner (tsx --test) — optional-chain with defaults.
export const CLIENT_CONFIG = import.meta.env?.VITE_CLIENT_CONFIG || 'yeshuman';

/** The pack manifest's `domain` (e.g. `health`), injected at build time; '' when unknown. */
export const TENANT_DOMAIN: string = import.meta.env?.VITE_TENANT_DOMAIN || '';

/** Unused for Data bench craft UI (platform auth only). Kept for optional future gates. */
export const PLATFORM_SIM_UI_ENABLED =
  String(import.meta.env?.VITE_PLATFORM_SIM_UI_ENABLED || '').toLowerCase() === 'true';

// API configuration for backend connection
// VITE_API_URL: Absolute API base for cross-origin browser requests (e.g. split prod domains).
// Leave empty for local dev: Vite proxies /api, /agent, /_allauth, /accounts to UI_PROXY_TARGET
// (see ui/vite.config.ts) so the phone can use http://<LAN-IP>:300X without baking localhost into the bundle.
export const API_BASE_URL = (import.meta.env?.VITE_API_URL || '').trim();

/** Backend origin for fetch URLs. Empty when VITE_API_URL is unset (same-origin; Vite proxies /api, /agent, /_allauth, /accounts). Otherwise no trailing slash and no trailing /api. */
export const API_ORIGIN = API_BASE_URL
  ? API_BASE_URL.replace(/\/$/, '').replace(/\/api$/, '')
  : '';

// Import UI-specific client configurations
import uiConfig from '@tenant/ui-config';
import { EMPLOYER_ORGANISATION_NEW_SETUP_PATH } from '@/lib/employer-org-setup';

/** Tenant UI slice — known fields typed; the rest is config. */
type ClientUiSlice = {
  name: string
  brand: string
  titleVariations?: string[]
  sarcasticVariations?: string[]
} & Record<string, any>

// Single-tenant slice (this UI build). Legacy name kept for callers.
export const CURRENT_CLIENT = uiConfig as ClientUiSlice
export const CLIENT_CONFIGS = { [CLIENT_CONFIG]: CURRENT_CLIENT };

// Export variations from current client
export const titleVariations: string[] = CURRENT_CLIENT.titleVariations ?? [];
export const sarcasticVariations: string[] = CURRENT_CLIENT.sarcasticVariations ?? [];

// Animation flag - defaults to true if not specified (backward compatibility)
export const enableAnimation = (CURRENT_CLIENT as any).enableAnimation !== false;

/** Agent chat, threads, memories, and SSE — default on; set features.agent false to disable per client */
export const AGENT_UI_ENABLED = (CURRENT_CLIENT as any).features?.agent !== false;

/** Experimental live triage demo UI (e.g. intercept CTA on emergency cards) */
export const CALLS_TRIAGE_DEMO_UI_ENABLED =
  (CURRENT_CLIENT as any).features?.callsTriageDemo === true;

/** Daily.co live interviews — opt-in via `features.daily.enabled`. */
export const DAILY_INTERVIEW_ENABLED =
  (CURRENT_CLIENT as { features?: { daily?: { enabled?: boolean } } }).features?.daily
    ?.enabled === true;

export type CoverBannerSurface = 'profile' | 'organisation'

/** Profile/org cover image strip — opt-in per tenant via `features.coverBanner`. */
export function isCoverBannerEnabled(surface: CoverBannerSurface): boolean {
  const coverBanner = (CURRENT_CLIENT as { features?: { coverBanner?: Partial<Record<CoverBannerSurface, boolean>> } })
    .features?.coverBanner
  return coverBanner?.[surface] === true
}

export type ListPaginationSurface =
  | 'employerJobs'
  | 'candidateBrowse'
  | 'candidateShortlist'
  | 'candidateApplied'
  | 'jobInvite'

export type ListPaginationConfig = {
  enabled: boolean
  pageSize: number
}

const DEFAULT_LIST_PAGINATION: ListPaginationConfig = {
  enabled: false,
  pageSize: 20,
}

/** Per-surface list pagination — `features.listPagination` in client-config. */
export function getListPagination(surface: ListPaginationSurface): ListPaginationConfig {
  const entry = (
    CURRENT_CLIENT as {
      features?: {
        listPagination?: Partial<
          Record<ListPaginationSurface, { enabled?: boolean; pageSize?: number }>
        >
      }
    }
  ).features?.listPagination?.[surface]
  if (!entry) return DEFAULT_LIST_PAGINATION
  const pageSize =
    typeof entry.pageSize === 'number' && entry.pageSize > 0 ? entry.pageSize : 20
  return {
    enabled: entry.enabled === true,
    pageSize,
  }
}

export {
  isProfileSectionEnabled,
  resolveProfileFeature,
  type ProfileMode,
  type ProfileSectionKey,
  type ProfileSections,
  type ResolvedProfileFeature,
} from '@/constants/profile-feature'

type OpportunityIngestFeatures = {
  enabled?: boolean
  /** Provider keys from pack config (e.g. adzuna, jooble, careerjet). */
  providers?: string[]
}

function opportunityIngestConfig(): OpportunityIngestFeatures | undefined {
  return (CURRENT_CLIENT as { features?: { opportunityIngest?: OpportunityIngestFeatures } })
    .features?.opportunityIngest
}

/** Marketplace ingest + UI marketplace surfaces (opt-in via client-config). */
export function isOpportunityIngestEnabled(): boolean {
  return opportunityIngestConfig()?.enabled === true
}

/**
 * Enabled marketplace ingest provider keys from `features.opportunityIngest.providers`.
 * Empty when unset — admin UI still works; Sync fans out from the API using the same config.
 */
export function getOpportunityIngestProviders(): string[] {
  const providers = opportunityIngestConfig()?.providers
  if (!Array.isArray(providers)) return []
  return providers
    .map((key) => String(key || '').trim().toLowerCase())
    .filter(Boolean)
}

type AlphaTestFeatures = {
  enabled?: boolean
  captureMarketplaceApply?: boolean
}

function alphaTestConfig(): AlphaTestFeatures | undefined {
  return (CURRENT_CLIENT as { features?: { alphaTest?: AlphaTestFeatures } }).features?.alphaTest
}

/** Master switch for tenant alpha-test gates (`features.alphaTest`). */
export function isAlphaTestEnabled(): boolean {
  return alphaTestConfig()?.enabled === true
}

/**
 * Marketplace Apply captures interest and does not open Adzuna redirect.
 * Requires `features.alphaTest.enabled` and `captureMarketplaceApply`.
 */
export function isMarketplaceApplyCaptureEnabled(): boolean {
  const alphaTest = alphaTestConfig()
  return alphaTest?.enabled === true && alphaTest?.captureMarketplaceApply === true
}

/** Alpha feedback board (shared anonymous items + upvotes + agent intake). Opt-in per tenant. */
export function isProductFeedbackEnabled(): boolean {
  return (
    (CURRENT_CLIENT as { features?: { productFeedback?: { enabled?: boolean } } }).features
      ?.productFeedback?.enabled === true
  )
}

/** Icon-only feedback shortcut in the header chrome (early-release discoverability). */
export function isProductFeedbackHeaderIconEnabled(): boolean {
  const productFeedback = (
    CURRENT_CLIENT as { features?: { productFeedback?: { enabled?: boolean; headerIcon?: boolean } } }
  ).features?.productFeedback
  return productFeedback?.enabled === true && productFeedback?.headerIcon === true
}

/** Who is asking feedback questions — agent copy and admin UI. */
export function getFeedbackRequesterLabel(): string {
  const productFeedback = (
    CURRENT_CLIENT as {
      name?: string
      features?: { productFeedback?: { requesterLabel?: string } }
    }
  ).features?.productFeedback
  const explicit = productFeedback?.requesterLabel?.trim()
  if (explicit) return explicit
  const name = (CURRENT_CLIENT as { name?: string }).name?.trim()
  if (name) return `${name} product & dev team`
  return 'the product & dev team'
}

/**
 * Persisted employer “managed organisation” context + sidebar switcher (UI).
 * Opt-in per pack via `features`.
 */
export function managedEmployerOrganisationSwitcherEnabled(): boolean {
  return (
    (CURRENT_CLIENT as { features?: { managedEmployerOrganisationSwitcher?: boolean } }).features
      ?.managedEmployerOrganisationSwitcher === true
  )
}

/** Company lookup and drafted details on the organisation onboarding steps — pack sets `features.organisationPrefill`. */
export function isOrganisationPrefillEnabled(): boolean {
  return (
    (CURRENT_CLIENT as { features?: { organisationPrefill?: { enabled?: boolean } } }).features
      ?.organisationPrefill?.enabled === true
  )
}

/** LinkedIn draft on candidate CV upload and profile review — pack sets `features.personPrefill`. */
export function isPersonPrefillEnabled(): boolean {
  return (
    (CURRENT_CLIENT as { features?: { personPrefill?: { enabled?: boolean } } }).features
      ?.personPrefill?.enabled === true
  )
}

/** Plate rich-text field editor (markdown round-trip) — opt-in per tenant via client-config. */
export function isRichTextEditorEnabled(): boolean {
  return (
    (CURRENT_CLIENT as { features?: { richTextEditor?: { enabled?: boolean } } }).features
      ?.richTextEditor?.enabled === true
  )
}

/** Shared tenant markdown documents — Yes Human v1. */
export function isDocumentsEnabled(): boolean {
  return (
    (CURRENT_CLIENT as { features?: { documents?: { enabled?: boolean } } }).features?.documents
      ?.enabled === true
  )
}

/** Conversational job brief. Pack sets `features.conversationalBrief`. */
export function isConversationalBriefEnabled(): boolean {
  return (
    (CURRENT_CLIENT as { features?: { conversationalBrief?: { enabled?: boolean } } }).features
      ?.conversationalBrief?.enabled === true
  )
}

/** API-wide markdown field limit (profile bio, org about, job description). */
export { MARKDOWN_FIELD_MAX_LENGTH } from '@/lib/markdown-field';

/** localStorage key: one active slug per CLIENT_CONFIG build. */
export function managedEmployerOrgSlugStorageKey(): string {
  return `ui.managedOrgSlug.${CLIENT_CONFIG}`
}

/** Employer focus dashboard (sidebar Home). */
export const EMPLOYER_HOME_PATH = '/employer'

/** Singular employer org hub when managed-org switcher is enabled. */
export const EMPLOYER_ORGANISATION_HUB_PATH = '/employer/organisation'

export function employerOrganisationHubHref(options?: { openCreate?: boolean }): string {
  if (!options?.openCreate) return EMPLOYER_ORGANISATION_HUB_PATH
  return EMPLOYER_ORGANISATION_NEW_SETUP_PATH
}

/** Plural employer org list route when switcher is disabled. */
export const EMPLOYER_ORGANISATIONS_LIST_PATH = '/employer/organisations'

/** Organisation settings landing: singular hub vs plural list by client flag. */
export function employerOrganisationHomePath(): string {
  return managedEmployerOrganisationSwitcherEnabled()
    ? EMPLOYER_ORGANISATION_HUB_PATH
    : EMPLOYER_ORGANISATIONS_LIST_PATH
}
/** Capitalize first letter of each word for display in headers and nav labels */
export function toTitleCase(str: string): string {
  if (!str?.trim()) return str;
  return str.replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Get page header title and description from routes config, with fallbacks. Uses route.title if set, else route.label, else default. Title is title-cased for display. */
export function getPageHeader(path: string, defaultTitle: string, defaultDescription: string) {
  const route = (CURRENT_CLIENT as any).routes?.[path];
  const title = route?.title ?? route?.label ?? defaultTitle;
  return {
    title: toTitleCase(title),
    description: route?.description ?? defaultDescription,
  };
}

// Animation constants
export const MATRIX_CHARS = 'アイウエオカキクケコサシスセソタチツテトナニヌネノハヒフヘホマミムメモヤユヨラリルレロワヲン';
export const NORMAL_CHARS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!?.,;:-_+=@#$%&*()[]{}|\\/<>';
export const RANDOM_CHARS = MATRIX_CHARS + NORMAL_CHARS;

// Animation timing constants
export const ANIMATION_STEPS = 20;
export const STEP_DURATION = 50;
export const VARIATION_DISPLAY_TIME = 4000;
export const SARCASTIC_DISPLAY_TIME = 5000;
export const MIN_ANIMATION_DELAY = 20000;
export const MAX_ANIMATION_DELAY = 120000;
export const FIRST_ANIMATION_MIN_DELAY = 10000;
export const FIRST_ANIMATION_MAX_DELAY = 20000;

// API constants — agent lives at site `/agent/` (not under `/api/`); use API_ORIGIN so `VITE_API_URL=…/api` still resolves correctly.
export const SSE_ENDPOINT = API_ORIGIN ? `${API_ORIGIN}/agent/stream/` : '/agent/stream/';
